// Simple string parsing for numeric op searching
// - supports both operator-prefixed queries (>=, <=, =, >, <) and plain numbers
// - supports positive and negative
// - supports arbitrary spaces between op and num
// - supports numbers with commas (e.g. "275,256" matches 275256)
// - ignores anything after the first number found
//   - "> 200       020" -> ">200"
//   - "236560" -> "=236560" (exact match)
//   - "275,256" -> "=275256" (exact match)
function parseAmountQuery(query) {
  const trimmed = query.trim();
  const operators = ['>=', '<=', '=', '>', '<'];

  // First try operator-prefixed query
  for (const op of operators) {
    if (trimmed.startsWith(op)) {
      const numberPart = trimmed.slice(op.length).trim().replace(/,/g, '');
      const value = parseFloat(numberPart);

      if (isNaN(value)) return null;

      return {operator: op, value};
    }
  }

  // If no operator found, try parsing as a plain number
  const cleanNumber = trimmed.replace(/,/g, '');
  const value = parseFloat(cleanNumber);
  if (!isNaN(value)) {
    return {operator: '=', value};
  }

  return null;
}

function fuzzyScore(text, query) {
  let tIndex = 0;
  let score = 0;

  for (let qIndex = 0; qIndex < query.length; qIndex++) {
    const qChar = query[qIndex];
    tIndex = text.indexOf(qChar, tIndex);
    if (tIndex === -1) return Infinity;
    score += tIndex;
    tIndex++;
  }

  return score;
}

export function filterByQuery(dataArray, query, options = {}) {
  const {numericField, textFields = [], roundFn = Math.round} = options;

  if (!dataArray || !query) return dataArray || [];

  const normalizedQuery = query.toLowerCase().trim();
  const numericQuery = normalizedQuery.replace(/,/g, '');

  // Try amount search first (with or without operators)
  const parsed = numericField ? parseAmountQuery(normalizedQuery) : null;
  if (numericField && parsed) {
    const {operator, value} = parsed;
    const amountMatches = dataArray.filter((item) => {
      let amount = parseFloat(item[numericField]);
      if (roundFn) {
        amount = roundFn(amount);
      }
      if (isNaN(amount)) return false;

      switch (operator) {
        case '>':
          return amount > value;
        case '<':
          return amount < value;
        case '>=':
          return amount >= value;
        case '<=':
          return amount <= value;
        case '=':
          return amount === value;
        default:
          return false;
      }
    });
    if (amountMatches.length > 0) return amountMatches;
  }

  // If no amount matches and query is purely numeric (after stripping commas), try document number
  if (numericQuery.match(/^\d+$/)) {
    return dataArray.filter((item) => {
      const docNum = item.document_number?.toString() || '';
      return docNum.includes(numericQuery);
    });
  }

  // Fuzzy match fallback
  return dataArray
    .map((item) => {
      const score = textFields.reduce((bestScore, field) => {
        let value = item[field];
        if (Array.isArray(value)) value = value.join(' ');
        else if (typeof value === 'number') value = value.toString();
        else if (typeof value !== 'string') value = '';

        const s = fuzzyScore(value.toLowerCase(), normalizedQuery);
        return s < bestScore ? s : bestScore;
      }, Infinity);

      return score === Infinity ? null : {item, score};
    })
    .filter(Boolean)
    .sort((a, b) => a.score - b.score)
    .map((r) => r.item);
}
